import { useEffect, useState } from 'react'
import { searchPortfolioMemories } from '../../api/portfolio'
import type { PortfolioMemoryEntry } from './types'

export function PortfolioMemories() {
  const [items, setItems] = useState<PortfolioMemoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await searchPortfolioMemories(query, 50)
      setItems(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load memories')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Initial load (empty query)
    load()
  }, [])

  if (loading) {
    return <div className="text-swiss-small text-swiss-gray-500">Loading memories...</div>
  }

  if (error) {
    return (
      <div className="border border-swiss-red bg-swiss-off-white p-4">
        <p className="text-swiss-small text-swiss-red mb-2">{error}</p>
        <button onClick={load} className="text-swiss-small underline underline-offset-2 cursor-pointer">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="border border-swiss-gray-200 bg-swiss-off-white p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-swiss-label uppercase tracking-widest text-swiss-gray-500">
          Memories
        </h2>
      </div>
      <div className="mb-3 flex items-center gap-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search memories"
          className="flex-1 border border-swiss-gray-200 bg-swiss-white px-2 py-1.5 text-swiss-small"
        />
        <button
          onClick={load}
          className="text-swiss-caption text-swiss-gray-500 underline underline-offset-2 cursor-pointer"
        >
          Search
        </button>
      </div>
      {items.length === 0 ? (
        <div className="border border-swiss-gray-200 bg-swiss-white p-4">
          <p className="text-swiss-small text-swiss-gray-500">
            {query.trim() ? 'No memories found for this query.' : 'No memory entries captured yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="border border-swiss-gray-200 bg-swiss-white p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-swiss-small text-swiss-black">{item.title}</p>
                <span className="text-swiss-caption font-swiss-mono text-swiss-gray-400">
                  {new Date(item.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="text-swiss-small text-swiss-gray-700 mt-2">{item.summary}</p>
              {!!item.tags?.length && (
                <p className="text-swiss-caption text-swiss-gray-500 mt-1">
                  {item.tags.join(' · ')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
